import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  IconButton, Menu, MenuItem, Tooltip, CircularProgress, Alert,
} from "@mui/material";
import { InfoOutlineRounded } from '@mui/icons-material';
import RefreshIcon from "@mui/icons-material/Refresh";
import { Select, Button, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import CloudNode from './cloudNode';
import Layout from "../../layout/layout";
import { api } from "../../services/api";
import { PropTypes } from 'prop-types';

const nodeTypes = { cloudNode: CloudNode };

// ✅ EDGE STYLING CONFIGURATION
const edgeStyles = {
  account: { stroke: '#000', strokeDasharray: '5,5', strokeWidth: 2 },
  region: { stroke: '#789867', strokeDasharray: '5,5', strokeWidth: 2 },
  vpc: { stroke: '#909090', strokeDasharray: '5,5', strokeWidth: 2 },
  subnet: { stroke: '#342578', strokeDasharray: '5,5', strokeWidth: 2 },
  securityGroup: { stroke: '#908f8f', strokeDasharray: '5,5', strokeWidth: 2 },
  ec2: { stroke: '#2d3436', strokeDasharray: '5,5', strokeWidth: 2 },
};

const CloudDiagram = ({ onSelectionChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark'; 

  /* -------------------- STATE -------------------- */
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  const [cloudData, setCloudData] = useState({
    regions: [], vpcs: {}, vpcCounts: {}, subnets: {}, subnetCounts: {},
    securityGroups: {}, securityGroupCounts: {}, ec2Instances: {},
    ec2InstanceCounts: {}, additionalDisks: {}, additionalDiskCounts: {},
  });

  const [selection, setSelection] = useState({
    regions: [], vpcs: [], subnets: [], securityGroups: [], ec2Instances: [],
  });

  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const navigate = useNavigate();
  const [resourceType, setResourceType] = useState("ec2");


  const selectedProvider = localStorage.getItem("selectedProvider") || localStorage.getItem("provider") || "aws";
  const isAzure = selectedProvider.toLowerCase().includes("azure");
  const accountId = isAzure ? "account-azure" : "account-aws";
  const accountLabel = isAzure ? "Azure Account" : "AWS Account";

  const handleRefreshResources = () => {
    localStorage.removeItem("cloud-flow-state");
    setExpandedNodes(new Set());
    setSelection({ regions: [], vpcs: [], subnets: [], securityGroups: [], ec2Instances: [] });
    setNodes(prev => [prev[0]]);
    setEdges([]);
    setLoading(true);
    
    api.get("/api/Resources/GetResourceArchitecture")
      .then(res => setCloudData(res.data))
      .finally(() => setLoading(false));
  };

  const hasSelection =
    selection.regions.length || selection.vpcs.length || selection.subnets.length ||
    selection.securityGroups.length || selection.ec2Instances.length;

  const buildContextFromSelection = () => {
    return {
      region: selection.regions[0] || null,
      vpc: selection.vpcs[0] || null,
      ec2Instance: selection.ec2Instances[0] || null,

      subnet: selection.subnets.length > 0 ? selection.subnets : [],
      securityGroup: selection.securityGroups.length > 0 ? selection.securityGroups : [],
    };
  };

  const handleCreateResource = () => {
    const resourceMap = {
      ec2: { resourceId: 4193, resourceName: "EC2" },
      s3: { resourceId: 47, resourceName: "S3 Bucket" },
      sg: { resourceId: 38, resourceName: "Security Group" },
    };

    const payload = {
      ...resourceMap[resourceType],
      context: buildContextFromSelection(),
      isEnabledonUI: true,
    };
    
    navigate("/Resources/Create_Request", { state: payload });
  };

  /* -------------------- LOAD API DATA -------------------- */
  useEffect(() => {
    async function loadData() {
      try {
        const response = await api.get("/api/Resources/GetResourceArchitecture");
        const data = response.data;
        setCloudData({
          regions: data.regions || [], vpcs: data.vpcs || {}, vpcCounts: data.vpcCounts || {},
          subnets: data.subnets || {}, subnetCounts: data.subnetCounts || {},
          securityGroups: data.securityGroups || {}, securityGroupCounts: data.securityGroupCounts || {},
          ec2Instances: data.ec2Instances || {}, ec2InstanceCounts: data.ec2InstanceCounts || {},
          additionalDisks: data.additionalDisks || {}, additionalDiskCounts: data.additionalDiskCounts || {},
        });
      } catch (err) {
        console.error("❌ Error loading architecture:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  /* -------------------- RESTORE FLOW -------------------- */
  useEffect(() => {
    if (loading) return;
    const saved = localStorage.getItem("cloud-flow-state");
    if (!saved) {
      setNodes([{
        id: accountId,
        type: 'cloudNode',
        position: { x: 400, y: 50 },
        data: { label: accountLabel, type: 'account', id:accountId, isSelected: false, hasChildren: true, isExpanded: expandedNodes.has(accountId) },
      }]);
      setEdges([]);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      if (parsed.nodes?.length) {
        setNodes(parsed.nodes);
        setEdges(parsed.edges || []);
        setExpandedNodes(new Set(parsed.expandedNodes || []));
        setSelection(parsed.selection || {});
      }
    } catch (err) {
      console.error("❌ Error restoring flow:", err);
    }
  }, [loading]);

  useEffect(() => {
    if (loading || !nodes.length) return;
    localStorage.setItem("cloud-flow-state", JSON.stringify({ nodes, edges, expandedNodes: Array.from(expandedNodes), selection }));
  }, [nodes, edges, expandedNodes, selection, loading]);

  useEffect(() => {
    setNodes(prev => prev.map(node => ({
      ...node, data: { ...node.data, isExpanded: expandedNodes.has(node.id) }
    })));
  }, [expandedNodes]);

  // ========== HELPER FUNCTIONS ==========
  const toggleInArray = (arr, val) => {
    if (!val) return;
    const idx = arr.indexOf(val);
    if (idx === -1) arr.push(val);
    else arr.splice(idx, 1);
  };

  const getNodeSelectedState = (nodeId, type, sel) => {
    const mapping = { region: sel.regions, vpc: sel.vpcs, subnet: sel.subnets, securityGroup: sel.securityGroups, ec2Instance: sel.ec2Instances };
    return mapping[type]?.includes(nodeId) || false;
  };

  const handleSelectionChange = (newSelection) => {
    const safeSelection = {
      regions: newSelection.regions || [], vpcs: newSelection.vpcs || [],
      subnets: newSelection.subnets || [], securityGroups: newSelection.securityGroups || [],
      ec2Instances: newSelection.ec2Instances || [],
    };
    setSelection(safeSelection);
    onSelectionChange?.(safeSelection);
    setNodes(prev => prev.map(node => ({ ...node, data: { ...node.data, isSelected: getNodeSelectedState(node.id, node.data.type, safeSelection) } })));
  };


  /* ================== UNIFIED CLICK HANDLER ================== */
  /* ================== UNIFIED CLICK HANDLER ================== */
  const handleNodeClick = useCallback((event, node) => {
    if (!node || !node.data) return;
    
    // ✅ NEW: Immediately exit if the node is an EC2 Instance
    if (node.data.type === 'ec2Instance') return; 

    const nodeId = node.id;
    const type = node.data.type;
    const isCtrlPressed = event?.ctrlKey || event?.metaKey;

    // --- 1. SELECTION LOGIC ---
    if (type !== "account" && type !== "disk") {
      const isMultiSelect = isCtrlPressed || type === "subnet" || type === "securityGroup";
      
      const newSel = structuredClone(selection);
      const typeMap = { region: 'regions', vpc: 'vpcs', subnet: 'subnets', securityGroup: 'securityGroups', ec2Instance: 'ec2Instances' };
      
      const arrayName = typeMap[type];

      if (arrayName) {
        if (isMultiSelect) {
          toggleInArray(newSel[arrayName], nodeId);
        } else {
          newSel[arrayName] = [nodeId];
        }
      }

      handleSelectionChange(newSel);
    }

    // --- 2. EXPANSION LOGIC ---
    if (type === 'vpc') {
      localStorage.removeItem("cloud-flow-state");
      setNodes(prev => prev.filter(n => n.data.type === 'account' || n.data.type === 'region' || n.data.type === 'vpc'));
      setEdges(prev => prev.filter(e => e.source === 'account-aws' || cloudData.regions.includes(e.source)));
      setExpandedNodes(new Set([nodeId]));
      showSubnetsForVpc(nodeId);
      return;
    }

    const newExpanded = new Set(expandedNodes);
    
    if (expandedNodes.has(nodeId)) {
      newExpanded.delete(nodeId);
      if (type === 'account') collapseRegions();
      if (type === 'region') collapseVpcsForRegion(nodeId);
      if (type === 'vpc') collapseSubnetsForVpc(nodeId);
      if (type === 'subnet') collapseSecurityGroupsForSubnet(nodeId);
      if (type === 'securityGroup') collapseEc2InstancesForSecurityGroup(nodeId);
      if (type === 'ec2Instance') collapseDisksForEc2(nodeId);
    } else {
      newExpanded.add(nodeId);
      if (type === 'account') showRegions();
      if (type === 'region') showVpcsForRegion(nodeId);
      if (type === 'vpc') showSubnetsForVpc(nodeId);
      if (type === 'subnet') showSecurityGroupsForSubnet(nodeId);
      if (type === 'securityGroup') showEc2InstancesForSecurityGroup(nodeId);
      if (type === 'ec2Instance') showDisksForEc2(nodeId);
    }

    setExpandedNodes(newExpanded);
  }, [expandedNodes, selection, cloudData]);


  /* ================== CASCADING COLLAPSE LOGIC ================== */
  // Fixes floating orphan nodes by finding all deep children to remove
  const getDescendantIds = (startingIds, startType) => {
    let ids = new Set(startingIds);
    let currentIds = [...startingIds];

    if (startType === 'region') {
      const vpcs = currentIds.flatMap(id => cloudData.vpcs[id] || []);
      vpcs.forEach(id => ids.add(id));
      currentIds = vpcs;
      startType = 'vpc';
    }
    if (startType === 'vpc') {
      const subnets = currentIds.flatMap(id => cloudData.subnets[id] || []);
      subnets.forEach(id => ids.add(id));
      currentIds = subnets;
      startType = 'subnet';
    }
    if (startType === 'subnet') {
      const sgs = currentIds.flatMap(id => cloudData.securityGroups[id] || []);
      sgs.forEach(id => ids.add(id));
      currentIds = sgs;
      startType = 'securityGroup';
    }
    if (startType === 'securityGroup') {
      const ec2s = currentIds.flatMap(id => cloudData.ec2Instances[id] || []);
      ec2s.forEach(id => ids.add(id));
    }
    return ids;
  };

  const removeNodesAndEdges = (idsToRemove) => {
    setNodes(prev => prev.filter(n => {
      if (idsToRemove.has(n.id)) return false;
      if (n.data?.type === 'disk' && idsToRemove.has(n.id.split('-disk-')[0])) return false;
      return true;
    }));
    setEdges(prev => prev.filter(e => {
      if (idsToRemove.has(e.target)) return false;
      if (e.target.includes('-disk-') && idsToRemove.has(e.target.split('-disk-')[0])) return false;
      return true;
    }));
  };

  const collapseRegions = () => {
    setNodes(prev => prev.filter(n => n.data.type === "account"));
    setEdges([]);
  };

  const collapseVpcsForRegion = (regionId) => {
    const vpcs = regionId ? (cloudData.vpcs[regionId] || []) : Object.values(cloudData.vpcs).flat();
    removeNodesAndEdges(getDescendantIds(vpcs, 'vpc'));
  };

  const collapseSubnetsForVpc = (vpcId) => {
    const subnets = vpcId ? (cloudData.subnets[vpcId] || []) : Object.values(cloudData.subnets).flat();
    removeNodesAndEdges(getDescendantIds(subnets, 'subnet'));
  };

  const collapseSecurityGroupsForSubnet = (subnetId) => {
    const groups = subnetId ? (cloudData.securityGroups[subnetId] || []) : Object.values(cloudData.securityGroups).flat();
    removeNodesAndEdges(getDescendantIds(groups, 'securityGroup'));
  };

  const collapseEc2InstancesForSecurityGroup = (sgId) => {
    const instances = sgId ? (cloudData.ec2Instances[sgId] || []) : Object.values(cloudData.ec2Instances).flat();
    removeNodesAndEdges(new Set(instances));
  };

  const collapseDisksForEc2 = (ec2Id) => {
    if (!ec2Id) return;
    removeNodesAndEdges(new Set([ec2Id])); // Helper automatically kills disks attached to this EC2
  };

  /* ================== EXPANSION IMPLEMENTATIONS ================== */
  
  const showRegions = () => {
    collapseVpcsForRegion();
    const regionNodes = cloudData.regions.map((region, i) => ({
      id: region, type: 'cloudNode', position: { x: 200 + i * 300, y: 200 },
      data: { label: `${region} (${cloudData.vpcCounts?.[region] || 0})`, type: 'region', id: region, isSelected: selection.regions.includes(region), hasChildren: cloudData.vpcs[region]?.length > 0, isExpanded: expandedNodes.has(region) }
    }));
    setNodes(prev => [prev[0], ...regionNodes]);
    setEdges(cloudData.regions.map(region => ({ id: `account-${region}`, source: 'account-aws', target: region, type: 'smoothstep', style: edgeStyles.account })));
  };

  const showVpcsForRegion = (regionId) => {
    collapseSubnetsForVpc();
    collapseSecurityGroupsForSubnet();
    const vpcs = cloudData.vpcs[regionId] || [];
    const vpcNodes = vpcs.map((vpc, i) => ({
      id: vpc, type: 'cloudNode', position: { x: 100 + i * 200, y: 350 },
      data: { label: `${vpc.substring(4, 15)}... (${cloudData.subnetCounts?.[vpc] || 0})`, type: "vpc", id: vpc, hasChildren: cloudData.subnets[vpc]?.length > 0, isExpanded: expandedNodes.has(vpc), isSelected: selection.vpcs.includes(vpc) }
    }));
    const vpcEdges = vpcs.map(vpc => ({ id: `${regionId}-${vpc}`, source: regionId, target: vpc, type: 'smoothstep', style: edgeStyles.region }));
    setNodes(prev => [...prev.filter(n => n.data.type !== "vpc"), ...vpcNodes]);
    setEdges(prev => [...prev.filter(e => e.source !== regionId), ...vpcEdges]);
  };

  const showSubnetsForVpc = (vpcId) => {
    const subnets = cloudData.subnets[vpcId] || [];
    setNodes(prev => {
      const otherNodes = prev.filter(n => !subnets.includes(n.id));
      const currentCount = otherNodes.filter(n => n.data.type === "subnet").length;
      const subnetNodes = subnets.map((subnet, i) => ({
        id: subnet, type: 'cloudNode', position: { x: 100 + (currentCount + i) * 180, y: 500 },
        data: { label: `${subnet.substring(7, 15)}... (${cloudData.securityGroupCounts?.[subnet] || 0})`, type: "subnet", id: subnet, hasChildren: cloudData.securityGroups[subnet]?.length > 0, isExpanded: expandedNodes.has(subnet), isSelected: selection.subnets.includes(subnet) }
      }));
      return [...otherNodes, ...subnetNodes];
    });
    const subnetEdges = subnets.map(subnet => ({ id: `${vpcId}-${subnet}`, source: vpcId, target: subnet, type: 'smoothstep', style: edgeStyles.vpc }));
    setEdges(prev => [...prev.filter(e => e.source !== vpcId), ...subnetEdges]);
  };

  const showSecurityGroupsForSubnet = (subnetId) => {
    const groups = cloudData.securityGroups[subnetId] || [];
    setNodes(prev => {
      const otherNodes = prev.filter(n => !groups.includes(n.id));
      const currentCount = otherNodes.filter(n => n.data.type === "securityGroup").length;
      const sgNodes = groups.map((sg, i) => ({
        id: sg, type: 'cloudNode', position: { x: 100 + (currentCount + i) * 180, y: 650 },
        data: { label: `${sg.substring(3, 11)}... (${cloudData.ec2InstanceCounts?.[sg] || 0})`, type: "securityGroup", id: sg, hasChildren: cloudData.ec2Instances[sg]?.length > 0, isExpanded: expandedNodes.has(sg), isSelected: selection.securityGroups.includes(sg) }
      }));
      return [...otherNodes, ...sgNodes];
    });
    const sgEdges = groups.map(sg => ({ id: `${subnetId}-${sg}`, source: subnetId, target: sg, type: 'smoothstep', style: edgeStyles.subnet }));
    setEdges(prev => [...prev.filter(e => !groups.includes(e.target)), ...sgEdges]);
  };

  const showEc2InstancesForSecurityGroup = (sgId) => {
    const instances = cloudData.ec2Instances[sgId] || [];
    setNodes(prev => {
      // Prevents wiping out other sibling disks accidentally when rendering
      const otherNodes = prev.filter(n => {
        if (instances.includes(n.id)) return false;
        if (n.data?.type === 'disk' && instances.includes(n.id.split('-disk-')[0])) return false;
        return true;
      });
      const currentCount = otherNodes.filter(n => n.data.type === "ec2Instance").length;
      const ec2Nodes = instances.map((instance, i) => ({
        id: instance, type: 'cloudNode', position: { x: 100 + (currentCount + i) * 180, y: 800 },
        data: { label: `${instance.substring(2, 10)}... (${cloudData.additionalDiskCounts?.[instance] || 0})`, type: "ec2Instance", id: instance, hasChildren: (cloudData.additionalDisks?.[instance]?.length || 0) > 0, isExpanded: expandedNodes.has(instance), isSelected: selection.ec2Instances.includes(instance) }
      }));
      return [...otherNodes, ...ec2Nodes];
    });
    const ec2Edges = instances.map(instance => ({ id: `${sgId}-${instance}`, source: sgId, target: instance, type: 'smoothstep', style: edgeStyles.securityGroup }));
    setEdges(prev => [...prev.filter(e => !instances.includes(e.target) && !e.target.includes('-disk-')), ...ec2Edges]);
  };

  const showDisksForEc2 = (ec2Id) => {
    if (!ec2Id) return;
    const disks = cloudData.additionalDisks?.[ec2Id] || [];
    if (!disks.length) return;
    const diskCount = cloudData.additionalDiskCounts?.[ec2Id] || disks.length;
    const diskNodes = disks.map((disk, i) => ({
      id: `${ec2Id}-disk-${disk}`, type: 'cloudNode', position: { x: 100 + i * 140, y: 950 },
      data: { label: `Disk (${diskCount})`, type: "disk", id: disk, hasChildren: false, isExpanded: false, isSelected: false }
    }));
    const diskEdges = disks.map(disk => ({ id: `${ec2Id}-${disk}`, source: ec2Id, target: `${ec2Id}-disk-${disk}`, type: 'smoothstep', style: edgeStyles.ec2 }));
    setNodes(prev => [...prev, ...diskNodes]);
    setEdges(prev => [...prev, ...diskEdges]);
  };

  const onConnect = useCallback(params => setEdges(eds => addEdge(params, eds)), []);

  /* ================== RENDER: LOADING ================== */
  if (loading) {
    return (
      <Layout pageType="sideBar">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <Typography variant="h6">⏳ Loading architecture...</Typography>
        </Box>
      </Layout>
    );
  }

  /* ================== RENDER: MAIN UI ================== */
  return (
    <Layout pageType='sideBar' overflow="auto" >
      <Box sx={{ width: '100%', height: '100vh', position: 'relative' }}>
          
        {/* ----------- FOOTER AREA ----------- */}
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1, backgroundColor: isDark ? "background.paper" : "#e5f6fd", borderTop: 1, borderColor: "divider", width: "100%" }}
        >
          <Alert severity="info" icon={<InfoOutlineRounded fontSize="inherit" />} sx={{ minWidth: '300px', alignItems: 'center', '& .MuiAlert-message': { display: 'flex', alignItems: 'center', width: '100%', fontSize: '0.85rem' } }}>
            <Box>
              <Typography variant="caption" display="block" sx={{ mb: 0.3 }}>• <strong>Click</strong> nodes to expand/collapse</Typography>
              <Typography variant="caption" display="block">• <strong>Ctrl+Click</strong> to select/deselect</Typography>
            </Box>
          </Alert>

          <Box sx={{ display: "flex", gap: 2, height: "fit-content" }}>
            <Tooltip title="Refresh Architecture" arrow>
              <Button variant="outlined" onClick={handleRefreshResources} disabled={loading} sx={{ width: 32, height: 32, minWidth: 32, minHeight: 32, padding: 0, borderRadius: 1, color: isDark ? "#81c784" : "#2e7d32", borderColor: isDark ? "#81c784" : "#2e7d32", "&:hover": { borderColor: isDark ? "#a5d6a7" : "#1b5e20", backgroundColor: isDark ? "rgba(129, 199, 132, 0.16)" : "#00a00b", color: isDark ? "#a5d6a7" : "white" } }}>
                {loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </Button>
            </Tooltip>
          </Box> 
        </Box>

        {/* ✅ CREATE RESOURCE PANEL */}
        <Box sx={{ position: "absolute", top: 120, right: 16, zIndex: 99, width: 280, bgcolor: "background.paper", p: 2.5, borderRadius: "8px", boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.15)", border: 1, borderColor: "divider" }}>
          <Typography fontWeight={700} mb={1.5} fontSize="0.95rem">Create Resource</Typography>
          <Select fullWidth size="small" value={resourceType} onChange={(e) => setResourceType(e.target.value)} MenuProps={{ disablePortal: true, PaperProps: { sx: { zIndex: 1600 } } }} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5' } }}>
            <MenuItem value="ec2">EC2 Instance</MenuItem>
            <MenuItem value="s3">S3 Bucket</MenuItem>
            <MenuItem value="sg">Security Group</MenuItem>
          </Select>

          <Button fullWidth variant="contained" sx={{ mt: 1, bgcolor: hasSelection ? (isDark ? "#90caf9" : "#2b62a8") : (isDark ? "action.disabledBackground" : "#ccc"), color: hasSelection ? (isDark ? "#121212" : "#fff") : "text.disabled", fontWeight: 600, textTransform: "none", "&:hover": { bgcolor: hasSelection ? (isDark ? "#64b5f6" : "#1f4f8a") : (isDark ? "action.disabledBackground" : "#ccc") } }} disabled={!hasSelection} onClick={handleCreateResource}>
            {hasSelection ? "Create Resource" : "Select a node first"}
          </Button>
        </Box>

        {/* ✅ REACT FLOW CANVAS */}
        <ReactFlow
          nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeClick={handleNodeClick} nodeTypes={nodeTypes}
          nodesConnectable={false} nodesDraggable={true} edgesUpdatable={false} connectOnClick={false} elementsSelectable={true} fitView
          style={{ width: '100%', height: '100%', backgroundColor: isDark ? '#121212' : '#f8fafb' }}
        >
          <Background color={isDark ? "#444" : "#d0d7de"} gap={20} size={0.5} />
          <Controls style={{ backgroundColor: isDark ? '#1e1e1e' : '#ffffff', border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`, borderRadius: '8px' }} />
          <MiniMap style={{ backgroundColor: isDark ? '#1e1e1e' : '#ffffff', border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`, borderRadius: '8px' }} nodeColor={(node) => { const colorMap = { 'account': isDark ? '#66b2ff' : '#0070f3', 'region': isDark ? '#69f0ae' : '#00b894', 'vpc': isDark ? '#ffe57f' : '#fdcb6e', 'subnet': isDark ? '#ffab91' : '#e17055', 'securityGroup': isDark ? '#b39ddb' : '#6c5ce7', 'ec2Instance': isDark ? '#64b5f6' : '#0984e3', 'disk': isDark ? '#b0bec5' : '#2d3436' }; return colorMap[node.data?.type] || (isDark ? '#b0bec5' : '#636e72'); }} />
        </ReactFlow>
      </Box>
    </Layout>
  );
};

CloudDiagram.propTypes = {
  onSelectionChange: PropTypes.func,
};

export default CloudDiagram;